import { useEffect, useMemo, useState } from "react";

import { Box, LinearProgress, Typography } from "@mui/material";

type StepStatus = "pending" | "active" | "complete" | "error";

type ProgressStep = {
  id: string;
  label: string;
  status: StepStatus;
};

type Props = {
  isLoading: boolean;
  isComplete?: boolean;
  hasError?: boolean;
  skipVerification?: boolean;
  errorMessage?: string;
  compact?: boolean;
};

const UpgradeCategoryProgress = ({
  isLoading,
  isComplete = false,
  hasError = false,
  skipVerification = false,
  errorMessage = "",
  compact = false,
}: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const stepLabels = useMemo(
    () => [
      "Preparing property details",
      skipVerification
        ? "Applying admin verification bypass"
        : "Generating security verification",
      "Opening subscription checkout",
    ],
    [skipVerification],
  );

  useEffect(() => {
    if (!isLoading) {
      if (isComplete) {
        setActiveIndex(stepLabels.length);
      }
      return undefined;
    }

    setActiveIndex(0);
    const interval = window.setInterval(() => {
      setActiveIndex((current) =>
        current >= stepLabels.length - 1 ? current : current + 1,
      );
    }, 1600);

    return () => window.clearInterval(interval);
  }, [isLoading, isComplete, stepLabels.length]);

  const steps: ProgressStep[] = stepLabels.map((label, index) => {
    if (hasError && index === Math.min(activeIndex, stepLabels.length - 1)) {
      return { id: String(index), label, status: "error" };
    }
    if (isComplete || index < activeIndex) {
      return { id: String(index), label, status: "complete" };
    }
    if (index === activeIndex && isLoading) {
      return { id: String(index), label, status: "active" };
    }
    return { id: String(index), label, status: "pending" };
  });

  const progressValue = isComplete
    ? 100
    : hasError
      ? Math.max(12, ((activeIndex + 0.35) / stepLabels.length) * 100)
      : Math.min(
          95,
          ((activeIndex + (isLoading ? 0.45 : 0)) / stepLabels.length) * 100,
        );

  const title = hasError
    ? "Unable to start upgrade"
    : isComplete
      ? "Upgrade ready"
      : "Setting up upgrade";

  const description = hasError
    ? errorMessage ||
      "We couldn't prepare this property for upgrade. Close and try again, or contact support if it keeps happening."
    : skipVerification
      ? "We're preparing this property and skipping owner verification per your admin selection."
      : "We're preparing this property and loading the next step of the upgrade flow.";

  const shellSx = compact
    ? { px: 0, py: 0 }
    : { px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 4 } };

  return (
    <Box sx={shellSx}>
      {!compact ? (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {description}
          </Typography>
        </>
      ) : null}

      <LinearProgress
        variant="determinate"
        value={progressValue}
        sx={{
          height: compact ? 6 : 8,
          borderRadius: 999,
          mb: compact ? 1 : 3,
          bgcolor: "rgba(13, 18, 11, 0.08)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 999,
            bgcolor: hasError ? "#d32f2f" : "#15803d",
          },
        }}
      />

      {compact ? (
        <Typography
          variant="caption"
          color={hasError ? "error" : "text.secondary"}
          sx={{ display: "block", mb: hasError ? 0 : 1 }}
        >
          {hasError ? description : isComplete ? "Ready" : description}
        </Typography>
      ) : null}

      {!compact ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {steps.map((step) => (
          <Box
            key={step.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              opacity: step.status === "pending" ? 0.55 : 1,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                flexShrink: 0,
                bgcolor:
                  step.status === "complete"
                    ? "#15803d"
                    : step.status === "active"
                      ? "#0395ff"
                      : step.status === "error"
                        ? "#d32f2f"
                        : "rgba(13, 18, 11, 0.2)",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: step.status === "active" ? 600 : 400,
                color:
                  step.status === "error"
                    ? "error.main"
                    : step.status === "active"
                      ? "text.primary"
                      : "text.secondary",
              }}
            >
              {step.label}
            </Typography>
          </Box>
        ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default UpgradeCategoryProgress;
